import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  CircularProgress,
  Alert,
  Pagination,
  Divider,
  Chip,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Edit, Delete, Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { useUserManagement } from '../hooks/useUserManagement';
import UserForm from './UserForm';
import { User, UserRole, UserStatus, getUserFullName, getUserRoles, ROLE_LABELS } from '../types/user';

const ITEMS_PER_PAGE = 8;

export const UsersTab: React.FC = () => {
  const {
    users,
    loading,
    error,
    selectedUser,
    setSelectedUser,
    handleEditUser,
    handleCreateUser,
    handleUpdateUser,
    handleDeleteUser
  } = useUserManagement();

  const [page, setPage] = useState(1);
  const [selectedRole, setSelectedRole] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [userFormOpen, setUserFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'add' | 'edit'>('add');

  const roles = useMemo(() => {
    const uniqueRoles = new Set<UserRole>();
    users.forEach((user: User) => {
      getUserRoles(user).forEach((role) => uniqueRoles.add(role));
    });
    return Array.from(uniqueRoles);
  }, [users]);

  const filteredUsers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return users.filter((user: User) => {
      const userRoles = getUserRoles(user);
      if (selectedRole !== 'all' && !userRoles.includes(selectedRole as UserRole)) {
        return false;
      }
      if (!query) return true;

      const fullName = getUserFullName(user).toLowerCase();
      const rolesText = userRoles.map((r) => ROLE_LABELS[r] || r).join(' ').toLowerCase();
      return (
        fullName.includes(query) ||
        user.email?.toLowerCase().includes(query) ||
        rolesText.includes(query) ||
        userRoles.some((r) => r.toLowerCase().includes(query))
      );
    });
  }, [users, selectedRole, searchQuery]);

  const paginatedUsers = filteredUsers.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleRoleChange = (event: SelectChangeEvent) => {
    setSelectedRole(event.target.value);
    setPage(1);
  };

  const handleAddUser = () => {
    setFormMode('add');
    setSelectedUser(null);
    setUserFormOpen(true);
  };

  const handleUserSubmit = async (userData: any) => {
    try {
      if (formMode === 'add') {
        await handleCreateUser(userData);
      } else {
        const payload = {
          ...userData,
          id: userData.id || selectedUser?.id,
        };
        if (!payload.id) {
          console.error('Нет id пользователя для обновления');
          return;
        }
        await handleUpdateUser(payload);
      }
      setUserFormOpen(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Ошибка при сохранении пользователя:', error);
    }
  };

  const getStatusChip = (status: UserStatus) => {
    switch (status) {
      case UserStatus.ACTIVE:
        return <Chip label="Активен" color="success" />;
      case UserStatus.INACTIVE:
        return <Chip label="Неактивен" color="error" />;
      default:
        return <Chip label={status} />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 3,
        pb: 2,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', flex: 1 }}>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 500 }}>
            Пользователи
          </Typography>
          <TextField
            size="small"
            placeholder="Поиск по ФИО, email, роли…"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            sx={{ minWidth: 240, flex: 1, maxWidth: 360 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Фильтр по роли</InputLabel>
            <Select
              value={selectedRole}
              label="Фильтр по роли"
              onChange={handleRoleChange}
            >
              <MenuItem value="all">Все роли</MenuItem>
              {roles.map((role: UserRole) => (
                <MenuItem key={role} value={role}>
                  {ROLE_LABELS[role] || role}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Button 
          variant="contained" 
          onClick={handleAddUser}
          startIcon={<AddIcon />}
          sx={{ 
            borderRadius: 2,
            textTransform: 'none',
            px: 3
          }}
        >
          Добавить пользователя
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      ) : (
        <>
          <Paper elevation={0} sx={{ 
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden'
          }}>
            <List sx={{ p: 0 }}>
              {paginatedUsers.length === 0 ? (
                <ListItem>
                  <ListItemText primary="Ничего не найдено" />
                </ListItem>
              ) : (
                paginatedUsers.map((user: User) => (
                  <React.Fragment key={user.id}>
                    <ListItem
                      sx={{
                        py: 2,
                        px: 3,
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        }
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography component="div" variant="subtitle1" sx={{ fontWeight: 500 }}>
                              {getUserFullName(user)}
                            </Typography>
                            {getStatusChip(user.status)}
                          </Box>
                        }
                        secondary={
                          <Box component="div" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Box component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography component="div" variant="body2" color="text.secondary">
                                Email:
                              </Typography>
                              <Typography component="div" variant="body2">
                                {user.email}
                              </Typography>
                            </Box>
                            <Box component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography component="div" variant="body2" color="text.secondary">
                                Роли:
                              </Typography>
                              {getUserRoles(user).map((role) => (
                                <Chip
                                  key={role}
                                  size="small"
                                  label={ROLE_LABELS[role] || role}
                                />
                              ))}
                            </Box>
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton 
                            edge="end" 
                            onClick={() => {
                              handleEditUser(user);
                              setFormMode('edit');
                              setUserFormOpen(true);
                            }}
                            sx={{ 
                              color: 'primary.main',
                              '&:hover': { backgroundColor: 'primary.lighter' }
                            }}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton 
                            edge="end" 
                            onClick={() => handleDeleteUser(user.id)}
                            sx={{ 
                              color: 'error.main',
                              '&:hover': { backgroundColor: 'error.lighter' }
                            }}
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))
              )}
            </List>
          </Paper>

          {filteredUsers.length > ITEMS_PER_PAGE && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={Math.ceil(filteredUsers.length / ITEMS_PER_PAGE)}
                page={page}
                onChange={handlePageChange}
                color="primary"
                shape="rounded"
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: 1
                  }
                }}
              />
            </Box>
          )}
        </>
      )}

      <UserForm
        open={userFormOpen}
        onClose={() => {
          setUserFormOpen(false);
          setSelectedUser(null);
        }}
        onSubmit={handleUserSubmit}
        initialData={selectedUser || undefined}
        mode={formMode}
      />
    </Box>
  );
}; 